import { useCallback, useEffect, useRef } from 'react';

import { ConnectorPlacementButton } from 'app/modules/wallforming/components/setting-container/connector-placement/ConnectorPlacementButton';
import { useHandleConnectorPlacement } from 'app/modules/wallforming/components/setting-container/hooks/useHandleConnectorPlacement';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { Article } from 'app/modules/wallforming/components/scenes/objects/articles/types/article';

interface ConnectorPlacementActionProps {
    articleDataLoaded: boolean;
}

export const ConnectorPlacementAction = ({ articleDataLoaded }: ConnectorPlacementActionProps) => {
    const placementButtonRef = useRef<HTMLButtonElement | null>(null);
    const selectedPoiIndexRef = useRef<number | undefined>(usePlacementStore.getState().poiSelection.current.index);
    const articlesRef = useRef<Record<string, Article>>(usePlacementStore.getState().articles);
    const isAutoConnectorEnabledRef = useRef<boolean>(true);

    const { handleConnectorPlacementStart, isLoading } = useHandleConnectorPlacement();

    const updateActionIcon = useCallback(() => {
        isAutoConnectorEnabledRef.current =
            articleDataLoaded && selectedPoiIndexRef.current !== undefined && !isLoading;

        if (placementButtonRef.current) {
            placementButtonRef.current.disabled = !isAutoConnectorEnabledRef.current;
        }
    }, [articleDataLoaded, isLoading]);

    useEffect(() => {
        const subscribedSelectedPoiIndex = usePlacementStore.subscribe(
            (state) => state.poiSelection.current.index,
            (newPoiIndex) => {
                selectedPoiIndexRef.current = newPoiIndex;
                updateActionIcon();
            }
        );

        return () => {
            subscribedSelectedPoiIndex();
        };
    }, [articlesRef, selectedPoiIndexRef, updateActionIcon]);

    return (
        <ConnectorPlacementButton
            articleDataLoaded={articleDataLoaded}
            handleConnectorPlacementStart={handleConnectorPlacementStart}
            isAutoConnectorEnabledRef={isAutoConnectorEnabledRef}
            isLoading={isLoading}
            placementButtonRef={placementButtonRef}
            updateActionIcon={updateActionIcon}
        />
    );
};
